import { useEffect, CSSProperties, useState, useMemo, useDeferredValue, useCallback } from 'react';
import useLiveStream, { ParticipantData, parseParticipantIdentity } from 'hooks/useLiveStream';
import Tile from './Tile';
import { Attendee } from 'common/api/proctorType';
import AttendeePane from 'common/components/AttendeePane';
import AttendeeDetail from 'common/components/AttendeeDetail';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { addBreadcrumb } from '../../common/slice/breadcrumbsSlice';
import { isApprovalRequired, isPauseAllowed } from 'helpers/prechecks';
import { Precheck, Session } from 'globals/interfaces';
import { hasUnreadMessages } from 'helpers/messages';
import { orderBy } from 'lodash';
import { RecordingTypes, SessionStatus } from 'globals/enums';
import { setActiveAttendee } from 'common/slice/activeAttendeeSlice';
import { getAvailableMediaSettings, isSessionEnded } from 'helpers/session';
import { useNavigate } from 'react-router-dom';
import { getCriticalAlertCount, hasUnreadCriticalAlerts } from 'helpers/alerts';
import { getSessionCompletedAt, getSessionJoinedAt } from 'helpers/sessionDetails';
import { setAvailableSettings } from 'common/slice/mediaSettingSlice';

interface Props {
  sessions: Session[];
  attendees: Attendee[];
  participant_group_id: number;
}

const TileViewContainer = ({ sessions, attendees, participant_group_id }: Props) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { attendee_id: attendeeId, isDetailView } = useAppSelector((state) => state.activeAttendee);
  const availableSettings = useAppSelector((state) => state.mediaSettings.availableSettings);
  const [availableHeight, setAvailableHeight] = useState(window.innerHeight - 60);
  const [availableWidth, setAvailableWidth] = useState((window.innerWidth * 7) / 12);
  const [reconnectingParticipants, setReconnectingParticipants] = useState({});

  const handleReconnectingStatus = (participant: any, isReconnecting: boolean) => {
    const { session_id } = parseParticipantIdentity(participant.identity);
    if (!session_id) return;
    setReconnectingParticipants((prev) => ({
      ...prev,
      [session_id]: isReconnecting,
    }));
  };

  const participants = useLiveStream(participant_group_id, handleReconnectingStatus);
  const deferredSessions = useDeferredValue(sessions);
  const deferredParticipants = useDeferredValue(participants);

  const filteredSessions = useMemo(() => {
    if (!deferredSessions?.length) return [];
    return deferredSessions.filter(
      (session: Session) => session.session_status !== SessionStatus.NoShow,
    );
  }, [deferredSessions]);

  const sortedSessions = useMemo(() => {
    if (!filteredSessions.length) return [];
    return orderBy(filteredSessions, ['attendee_id', ['asc']]);
  }, [filteredSessions]);

  const participantsMap = useMemo(() => {
    const map = new Map<string, ParticipantData>();
    deferredParticipants?.forEach((participant: ParticipantData) => {
      map.set(participant.session_id, participant);
    });
    return map;
  }, [deferredParticipants]);

  const attendeesMap = useMemo(() => {
    const map = new Map<number, Attendee>();
    attendees?.forEach((attendee: Attendee) => map.set(attendee.attendee_id, attendee));
    return map;
  }, [attendees]);

  const sessionsByAttendeeId = useMemo(() => {
    const map = new Map<number, Session>();
    deferredSessions?.forEach((session: Session) => map.set(session.attendee_id, session));
    return map;
  }, [deferredSessions]);

  const participantsLength = sortedSessions.length;

  const { rows, columns } = useMemo(() => {
    let computedRows = 2;
    let computedColumns = 1;
    if (participantsLength >= 3 && participantsLength <= 4) {
      computedRows = 2;
      computedColumns = 2;
    } else if (participantsLength >= 5 && participantsLength <= 9) {
      computedRows = 3;
      computedColumns = 3;
    } else if (participantsLength >= 10 && participantsLength <= 16) {
      computedRows = 4;
      computedColumns = 4;
    } else if (participantsLength >= 16 && participantsLength <= 24) {
      computedRows = 5;
      computedColumns = 4;
    }
    return { rows: computedRows, columns: computedColumns };
  }, [participantsLength]);

  const rowHeight = useMemo(() => {
    if (!rows) return availableHeight;
    return availableHeight / rows - rows * 5;
  }, [availableHeight, rows]);

  const columnsWidth = useMemo(() => {
    if (!columns) return availableWidth;
    return availableWidth / columns - columns * 5;
  }, [availableWidth, columns]);

  const gridStyle: CSSProperties = useMemo(
    () => ({
      display: 'grid',
      gridTemplateRows: `repeat(${rows}, ${rowHeight}px)`,
      gridTemplateColumns: `repeat(${columns}, ${columnsWidth}px)`,
      gap: '5px',
      maxHeight: `${availableHeight}px`,
      justifyContent: 'center',
      alignItems: 'center',
    }),
    [rows, columns, rowHeight, columnsWidth, availableHeight],
  );

  const gridItemStyle: CSSProperties = useMemo(
    () => ({
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      position: 'relative',
      width: `${columnsWidth}px`,
      maxWidth: `${columnsWidth}px`,
      height: `${rowHeight}px`,
      maxHeight: `${rowHeight}px`,
      backgroundColor: '#0F172A',
    }),
    [columnsWidth, rowHeight],
  );

  useEffect(() => {
    const updateAvailableHeight = () => {
      setAvailableHeight(window.innerHeight - 60);
      setAvailableWidth((window.innerWidth * 7) / 12);
    };

    window.addEventListener('resize', updateAvailableHeight);

    return () => {
      window.removeEventListener('resize', updateAvailableHeight);
    };
  }, []);

  const handleTileClick = useCallback(
    (attendee: Attendee) => {
      if (!attendee) return;
      const session = sessionsByAttendeeId.get(attendee.attendee_id);
      if (!session) return;
      const payload = {
        label: attendee?.attendee_external_id,
        url: '',
      };
      if (isSessionEnded(session?.session_status)) {
        dispatch(addBreadcrumb(payload));
        navigate(`/session/${session.session_uuid}`);
      } else {
        dispatch(addBreadcrumb(payload));
        dispatch(setActiveAttendee({ ...attendee, isDetailView: true }));
      }
    },
    [dispatch, navigate, sessionsByAttendeeId],
  );

  const renderAttendeeDetailView = () => {
    const session = sessionsByAttendeeId.get(attendeeId);
    if (session && session?.session_status !== SessionStatus.NoShow) {
      const attendee = attendeesMap.get(attendeeId);
      return (
        <AttendeeDetail
          participants={participants}
          attendee={attendee}
          sessionId={session?.session_id}
          session={session}
          isReconnecting={reconnectingParticipants[session?.session_id]}
        />
      );
    }

    return (
      <div className="flex items-center justify-center h-full">
        <p className="flex items-center gap-1 px-3 py-2 text-sm text-white bg-blue-500 rounded">
          {session?.session_status === SessionStatus.NoShow
            ? 'No Show'
            : 'Candidate has not joined yet'}
        </p>
      </div>
    );
  };

  const sessionMediaSettings = useMemo(() => {
    if (!deferredSessions?.length) return [];
    const sessionWithConfiguration = deferredSessions.find((session) => session.configuration);
    return sessionWithConfiguration
      ? getAvailableMediaSettings(sessionWithConfiguration.configuration)
      : [];
  }, [deferredSessions]);

  const deferredSessionCount = deferredSessions?.length || 0;
  const availableSettingsCount = availableSettings.length;

  useEffect(() => {
    if (sessionMediaSettings.length && !availableSettingsCount && deferredSessionCount) {
      dispatch(setAvailableSettings(sessionMediaSettings));
    }
  }, [sessionMediaSettings, availableSettingsCount, deferredSessionCount, dispatch]);

  return attendeeId && isDetailView ? (
    renderAttendeeDetailView()
  ) : (
    <div style={gridStyle}>
      {sortedSessions.length > 0 &&
        sortedSessions.map((session: Session, index) => {
          const participant = participantsMap.get(session.session_id);
          const prechecks: Precheck[] = session.pre_check ? Object.values(session.pre_check) : [];
          const isApprovalPending = isApprovalRequired(prechecks, session.configuration);
          const isPauseEnabled = isPauseAllowed(prechecks, session.configuration);
          const attendee = attendeesMap.get(session.attendee_id);
          if (!attendee) {
            return null;
          }
          const messages = session?.messages ? Object.values(session.messages) : [];
          const alerts = session?.alerts ? Object.values(session.alerts) : [];
          const sessionCompletedAt = getSessionCompletedAt(alerts);
          const sessionJoinedAt = getSessionJoinedAt(alerts);
          const isChatUnread = hasUnreadMessages(messages);
            const isCriticalAlertUnread = hasUnreadCriticalAlerts(alerts);
            const criticalAlertCount = getCriticalAlertCount(alerts);
            return (
              <div
                key={session.attendee_id}
              style={gridItemStyle}
              onClick={() => handleTileClick(attendee)}
              data-testid={`tile_${index}`}
            >
              <div
                style={{ height: 'calc(100% - 24px)' }}
                className="flex items-center justify-center w-full"
                >
                  <Tile
                    availableSettings={getAvailableMediaSettings(session.configuration)}
                    sessionStatus={session.session_status}
                    attendeeId={session.attendee_id}
                    participant={participant}
                    isApprovalPending={isApprovalPending}
                    isReconnecting={reconnectingParticipants[session.session_id]}
                    criticalAlertCount={criticalAlertCount}
                  />
                </div>
              <div className="flex flex-row items-center justify-between w-full h-6 p-2 bg-blue-100">
                <AttendeePane
                  sessionCompletedAt={sessionCompletedAt}
                  sessionJoinedAt={sessionJoinedAt}
                  tooltipId={`tile_tooltip_${index}`}
                  isChatUnread={isChatUnread}
                  isApprovalPending={isApprovalPending}
                  attendee={attendee}
                  sessionId={session.session_id}
                  sessionUuid={session.session_uuid}
                  sessionStatus={session?.session_status}
                  isCriticalAlertUnread={isCriticalAlertUnread}
                  isPaused={session?.pause}
                  secondaryCamera={session?.configuration?.secondary_camera}
                  isSecondaryDisabled={session?.is_secondary_disabled}
                  isPauseAllowed={isPauseEnabled}
                />
              </div>
            </div>
          );
        })}
    </div>
  );
};

export default TileViewContainer;
